import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { X, FileText, Check, Eye, Menu, ChevronLeft, ChevronRight } from "lucide-react";
import { fetchPatientDetails, previewReport, finalizeReport, rejectReport } from "./Ecg_handlers";
import { API_BASE_URL } from "../Api/apiconnector";

const PatientReportPage = () => {
  const { patientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const doctorUsername = location.state?.doctorUsername;

  const [patient, setPatient] = useState(null);
  const [patientName, setPatientName] = useState("");
  const [patientIdNumber, setPatientIdNumber] = useState("");
  const [age, setAge] = useState("");
  const [testDate, setTestDate] = useState("");
  const [reportDate, setReportDate] = useState("");
  const [heartRate, setHeartRate] = useState("");
  const [gender, setGender] = useState("");
  const [ecgFindings, setEcgFindings] = useState("");
  const [customFinding, setCustomFinding] = useState("");
  const [additionalFindings, setAdditionalFindings] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("patient-info");
  const [showAIInterpretation, setShowAIInterpretation] = useState(false);
  const [aiInterpretation, setAiInterpretation] = useState("");

  // ECG dropdown options
  const ecgOptions = [
    "Normal ECG",
    "Sinus rhythm with incomplete RBBB",
    "Sinus Tachycardia with incomplete RBBB",
    "Sinus Bradycardia with incomplete RBBB",
    "Sinus Bradycardia",
    "Sinus Tachycardia",
    "Normal sinus rhythm with t inversion in lead III",
    "Other",
  ];

  const fetchAIInterpretation = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/ecg-ai-interpretation/${patientId}/`,
        {
          method: "GET",
          credentials: "include", // <-- Send Django session cookie
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (response.ok && data.show_ai_interpretation) {
        setShowAIInterpretation(true);
        setAiInterpretation(data.impression);
      } else {
        setShowAIInterpretation(false);
        setAiInterpretation("");
      }
    } catch (error) {
      console.error("Error fetching AI Interpretation:", error);
      setShowAIInterpretation(false);
      setAiInterpretation("");
    }
  };

  useEffect(() => {
    const loadPatientData = async () => {
      await fetchPatientDetails(patientId, doctorUsername, setPatient);

      await fetchAIInterpretation();
    };

    loadPatientData();
  }, [patientId, doctorUsername]);

  // Populate form fields when patient data is loaded
  useEffect(() => {
    if (patient) {
      setPatientName(patient.PatientName || "");
      setPatientIdNumber(patient.PatientId || patient.id || "");
      setAge(patient.age || "");
      setTestDate(patient.TestDate || "");
      setReportDate(patient.ReportDate || new Date().toLocaleDateString('en-GB').replace(/\//g, '-'));
      setHeartRate(patient.HeartRate || patient.heartRate || "");
      setGender(patient.gender || "");
    }
  }, [patient]);

  const handlePreview = async () => {
    const finalFindings = ecgFindings === "Other" ? customFinding : ecgFindings;

    if (!finalFindings.trim()) {
      alert("Please select or enter ECG findings");
      return;
    }

    setLoading(true);
    await previewReport(          // <-- CHANGE THIS
      patient.id,
      doctorUsername,
      finalFindings,
      additionalFindings,
      {
        name: patientName,
        patientId: patientIdNumber,
        age: age,
        gender: gender,
        testDate: testDate,
        reportDate: reportDate,
        heartRate: heartRate
      },
      setPreviewData
    );
    setLoading(false);

    // Scroll to preview section on mobile
    if (window.innerWidth < 768) {
      document.getElementById("preview-section")?.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const handleFinalize = async () => {
    const finalFindings = ecgFindings === "Other" ? customFinding : ecgFindings;
    
    if (!finalFindings.trim()) {
      alert("Please select or enter ECG findings");
      return;
    }

    if (!window.confirm("Are you sure you want to finalize this report?")) {
      return;
    }

    setLoading(true);
    const success = await finalizeReport(
      patient.id,
      doctorUsername,
      finalFindings,
      additionalFindings,
      {
        name: patientName,
        patientId: patientIdNumber,
        age: age,
        gender: gender,
        testDate: testDate,
        reportDate: reportDate,
        heartRate: heartRate
      }
    );
    setLoading(false);

    if (success) {
      navigate("/cardiologist-dashboard");
    }
  };


  const handleReject = async () => {
    const reason = window.prompt("Please enter rejection reason:");
  
    if (!reason || !reason.trim()) {
      alert("Rejection reason is required");
      return;
    }
  
    setLoading(true);
    const success = await rejectReport(
      patient.id,
      doctorUsername,
      reason
    );
    setLoading(false);
  
    if (success) {
      navigate("/cardiologist-dashboard");
    }
  };

  const handleClose = () => {
    if (window.confirm("Are you sure you want to leave? Any unsaved changes will be lost.")) {
      navigate("/cardiologist-dashboard");
    }
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setIsMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  if (!patient) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading patient details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Mobile Navigation Bar */}
      <div className="md:hidden bg-white shadow-sm sticky top-0 z-20">
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>
          
          <h2 className="text-lg font-bold text-gray-800 truncate max-w-[180px]">
            {patientName || 'Patient'}'s Report
          </h2>
          
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Navigation menu"
          >
            <Menu className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-lg border-t z-10">
            <div className="py-2">
              <button
                onClick={() => scrollToSection("patient-info")}
                className={`w-full px-4 py-3 text-left flex items-center justify-between ${
                  activeSection === "patient-info" ? "bg-blue-50 text-blue-600" : "hover:bg-gray-50"
                }`}
              >
                <span>Patient Info</span>
                <ChevronRight className="w-4 h-4" />
              </button>
              {patient.image && (
                <button
                  onClick={() => scrollToSection("ecg-image")}
                  className={`w-full px-4 py-3 text-left flex items-center justify-between ${
                    activeSection === "ecg-image" ? "bg-blue-50 text-blue-600" : "hover:bg-gray-50"
                  }`}
                >
                  <span>ECG Image</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => scrollToSection("ecg-findings")}
                className={`w-full px-4 py-3 text-left flex items-center justify-between ${
                  activeSection === "ecg-findings" ? "bg-blue-50 text-blue-600" : "hover:bg-gray-50"
                }`}
              >
                <span>ECG Findings</span>
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className="border-t px-4 py-3">
                <button
                  onClick={handlePreview}
                  disabled={loading}
                  className="w-full bg-amber-500 text-white py-3 rounded-lg hover:bg-amber-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 mb-2"
                >
                  <Eye className="w-5 h-5" />
                  {loading ? "Loading..." : "Preview PDF"}
                </button>
                <button
                  onClick={handleFinalize}
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                >
                  <Check className="w-5 h-5" />
                  {loading ? "Processing..." : "Finalize PDF"}
                </button>
                <button
                  onClick={handleReject}
                  disabled={loading}
                  className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 disabled:bg-gray-400 font-medium flex items-center justify-center gap-2"
                >
                  <X className="w-5 h-5" />
                  Reject
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
              {patientName || 'Patient'}'s Report
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 sm:px-6 mt-4 md:mt-6">
        {/* Patient Details Form Section */}
        <div id="patient-info" className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-4 md:mb-6">
          <h3 className="font-semibold text-lg md:text-lg mb-4 md:mb-6 text-gray-800">Patient Information</h3>
          
          {/* Form Grid */}
          <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-1 lg:grid-cols-3 md:gap-4 lg:gap-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                Name:
              </label>
              <input
                type="text"
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
                className="w-full px-3 py-2 md:py-2 border-b-2 border-gray-300 bg-white focus:outline-none focus:border-blue-500 text-base md:text-sm"
                placeholder="Enter patient name"
              />
            </div>
            
            {/* Patient ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                Patient ID:
              </label>
              <input
                type="text"
                value={patientIdNumber}
                onChange={(e) => setPatientIdNumber(e.target.value)}
                className="w-full px-3 py-2 md:py-2 border-b-2 border-gray-300 bg-white focus:outline-none focus:border-blue-500 text-base md:text-sm"
                placeholder="Enter patient ID"
              />
            </div>
            
            {/* Age */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                Age:
              </label>
              <input
                type="text"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                className="w-full px-3 py-2 md:py-2 border-b-2 border-gray-300 bg-white focus:outline-none focus:border-blue-500 text-base md:text-sm"
                placeholder="Enter age"
              />
            </div>

            {/* Test Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                Test Date:
              </label>
              <input
                type="text"
                value={testDate}
                onChange={(e) => setTestDate(e.target.value)}
                placeholder="DD-MM-YYYY"
                className="w-full px-3 py-2 md:py-2 border-b-2 border-gray-300 bg-white focus:outline-none focus:border-blue-500 text-base md:text-sm"
              />
            </div>
            
            {/* Report Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                Report Date:
              </label>
              <input
                type="text"
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
                placeholder="DD-MM-YYYY"
                className="w-full px-3 py-2 md:py-2 border-b-2 border-gray-300 bg-white focus:outline-none focus:border-blue-500 text-base md:text-sm"
              />
            </div>
            
            {/* Heart Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 md:mb-2">
                Heart Rate:
              </label>
              <input
                type="text"
                value={heartRate}
                onChange={(e) => setHeartRate(e.target.value)}
                className="w-full px-3 py-2 md:py-2 border-b-2 border-gray-300 bg-white focus:outline-none focus:border-blue-500 text-base md:text-sm"
                placeholder="Enter heart rate"
              />
            </div>
          </div>

          {/* Gender */}
          <div className="mt-6 md:mt-8">
            <label className="block text-sm font-medium text-gray-700 mb-2 md:mb-3">
              Gender:
            </label>
            <div className="flex flex-wrap gap-4 md:gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="Male"
                  checked={gender === "Male"}
                  onChange={(e) => setGender(e.target.value)}
                  className="w-4 h-4 md:w-4 md:h-4 text-blue-600 cursor-pointer"
                />
                <span className="text-gray-700 text-sm md:text-base">Male</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="Female"
                  checked={gender === "Female"}
                  onChange={(e) => setGender(e.target.value)}
                  className="w-4 h-4 md:w-4 md:h-4 text-blue-600 cursor-pointer"
                />
                <span className="text-gray-700 text-sm md:text-base">Female</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="Others"
                  checked={gender === "Others" || gender === "Other"}
                  onChange={(e) => setGender("Others")}
                  className="w-4 h-4 md:w-4 md:h-4 text-blue-600 cursor-pointer"
                />
                <span className="text-gray-700 text-sm md:text-base">Others</span>
              </label>
            </div>
          </div>
        </div>

        {/* ECG Image Section */}
        {patient.image && (
          <div id="ecg-image" className="bg-white rounded-lg shadow-md p-3 md:p-4 lg:p-6 mb-4 md:mb-6">
            <label className="font-semibold block mb-3 text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              ECG Image
            </label>
            <div className="flex justify-center overflow-x-auto bg-gray-50 p-2 rounded-lg">
              <img
                src={`${patient.image}`}
                alt="ECG"
                className="max-w-full h-auto max-h-[500px] border rounded-lg shadow-sm object-contain"
                loading="lazy"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">Swipe horizontally to view full image on mobile</p>
          </div>
        )}

        {showAIInterpretation && (
          <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-blue-700 mb-2">
              AI ECG Interpretation
            </h3>

            <p className="text-gray-800 whitespace-pre-wrap">
              {aiInterpretation}
            </p>
          </div>
        )}

        {/* Form Section */}
        <div id="ecg-findings" className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
          {/* ECG Findings Dropdown */}
          <div className="mb-6">
            <label className="font-semibold block mb-2 text-base md:text-lg">
              ECG Findings *
            </label>
            <select
              className="border border-gray-300 p-3 w-full rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-base md:text-sm"
              value={ecgFindings}
              onChange={(e) => setEcgFindings(e.target.value)}
            >
              <option value="">Select ECG Finding</option>
              {ecgOptions.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>

            {/* Custom Finding Input */}
            {ecgFindings === "Other" && (
              <div className="mt-3">
                <input
                  type="text"
                  placeholder="Enter custom ECG finding"
                  className="border border-gray-300 p-3 w-full rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-base md:text-sm"
                  value={customFinding}
                  onChange={(e) => setCustomFinding(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Additional Findings */}
          <div className="mb-6">
            <label className="font-semibold mb-2 block text-base md:text-lg">
              Additional Findings
            </label>
            <textarea
              placeholder="Enter any additional observations or notes..."
              className="border border-gray-300 w-full p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none text-base md:text-sm"
              rows="4"
              value={additionalFindings}
              onChange={(e) => setAdditionalFindings(e.target.value)}
            />
          </div>

          {/* Desktop Action Buttons */}
          <div className="hidden md:flex gap-3">
            <button
              onClick={handlePreview}
              disabled={loading}
              className="flex-1 bg-amber-500 text-white px-6 py-3 rounded-lg hover:bg-amber-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              <Eye className="w-5 h-5" />
              {loading ? "Loading..." : "Preview PDF"}
            </button>
            <button
              onClick={handleFinalize}
              disabled={loading}
              className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              {loading ? "Processing..." : "Finalize PDF"}
            </button>
            <button
              onClick={handleReject}
              disabled={loading}
              className="flex-1 bg-red-600 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-1 text-sm"
            >
              <X className="w-4 h-4" />
              Reject
            </button>
          </div>

          {/* Mobile Action Buttons (fixed bottom) */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-3 z-10">
            <div className="flex gap-2">
              <button
                onClick={handlePreview}
                disabled={loading}
                className="flex-1 bg-amber-500 text-white px-4 py-3 rounded-lg hover:bg-amber-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 text-sm"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
              <button
                onClick={handleFinalize}
                disabled={loading}
                className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 text-sm"
              >
                <Check className="w-4 h-4" />
                Finalize
              </button>
              <button
                onClick={handleReject}
                disabled={loading}
                className="flex-1 bg-red-600 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-1 text-sm"
              >
                <X className="w-4 h-4" />
                Reject
              </button>
            </div>
          </div>
        </div>

        {/* Preview Report Section */}
        {previewData && (
          <div id="preview-section" className="bg-white rounded-lg shadow-md p-4 md:p-6 relative mb-20 md:mb-0">
            <button
              onClick={() => setPreviewData(null)}
              className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 rounded-full transition-colors z-10"
              aria-label="Close preview"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            
            <h3 className="font-semibold text-lg md:text-xl mb-4 text-gray-800 flex items-center gap-2">
              <Eye className="w-5 h-5 md:w-6 md:h-6" />
              Report Preview
            </h3>
            
            <div className="prose max-w-none mb-4">
              <div className="text-gray-700 whitespace-pre-wrap leading-relaxed text-sm md:text-base bg-gray-50 p-4 rounded-lg overflow-x-auto">
                {previewData.report_text}
              </div>
            </div>

            {previewData.ecg_image_base64 && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Preview ECG Image:</p>
                <div className="flex justify-center overflow-x-auto bg-gray-50 p-2 rounded-lg">
                  <img
                    src={previewData.ecg_image_base64}
                    alt="ECG Preview"
                    className="max-w-full h-auto max-h-[400px] border rounded-lg shadow-sm object-contain"
                    loading="lazy"
                  />
                </div>
              </div>
            )}

            {/* Mobile Actions for Preview Section */}
            <div className="md:hidden flex gap-2 mt-6 pt-4 border-t">
              <button
                onClick={handleFinalize}
                disabled={loading}
                className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 text-sm"
              >
                <Check className="w-4 h-4" />
                Finalize Report
              </button>
              <button
                onClick={() => setPreviewData(null)}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium flex items-center justify-center gap-2 text-sm"
              >
                <X className="w-4 h-4" />
                Close Preview
              </button>
            </div>
          </div>
        )}

        {/* Spacing for mobile fixed buttons */}
        <div className="md:hidden h-16"></div>
      </div>
    </div>
  );
};

export default PatientReportPage;